import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { RangerApi } from '@/shared/api/ranger'
import { ApiError } from '@/shared/api/types'

/** Turns a list of food names into meals on the trip. Resolves once the meal plan on screen has caught up. */
export function useAddMealsWithRanger(tripId: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const api = useMemo(() => new RangerApi(getAccessToken), [getAccessToken])

  return useMutation({
    mutationFn: (foods: string) => api.addMealsFromFoods(tripId, foods),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['meals', tripId] }),
        queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
      ])
  })
}

/** Never shows raw backend or AI provider messages. A 400 is our own wording about the list. */
export function addMealsErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 400) return error.message
    if (error.status === 403) return 'You do not have permission to add meals to this trip.'
    if (error.status === 429) return 'Ranger is at capacity. Please wait a moment and try again.'
  }
  return 'Something went wrong with Ranger. Please try again in a few moments.'
}
